import { createApp, h } from 'vue';
import QRCode from 'qrcode';
import RecetaContenedor from '../components/global/receta-layout.vue';
import vuetify from '@/plugins/vuetify';
import { convert_to_ISO } from '@/utils/converte_date';
//import { getPractitioner, getDoctorInfoByCedula } from '@/api/practicante';
import { getPractitioner } from '@/api/practicante';
import { assignCuponCode, useCuponCode } from '@/api/cupon';

export function isControlledMed(med) {
    if (!med) return false;
    if (med.itemDescription === 'Sin medicamentos') return false;
    if (med.category === 'controlled') return true;
    if (med.category === 'normal') return false;
    if (med.itemGroup !== undefined && med.itemGroup !== null) return parseInt(med.itemGroup, 10) <= 3;
    if (med.grupo !== undefined && med.grupo !== null) return parseInt(med.grupo, 10) <= 3;
    if (!med.productFrequency || med.productFrequency == 0 || med.productFrequency === '0') return true;
    return false;
}

export async function printCustomPdf(recipeId, recetaData, prescriptionProps) {
    prescriptionProps.isPrint = true;

    // Regla de negocio: primero controlados, luego no controlados (excluyendo 'Sin medicamentos')
    if (recetaData?.medicamentos?.length) {
        recetaData.medicamentos = recetaData.medicamentos.filter(
            (m) => m.itemDescription && m.itemDescription !== 'Sin medicamentos',
        );
        const controlledMeds = recetaData.medicamentos.filter((m) => isControlledMed(m));
        const normalMeds = recetaData.medicamentos.filter((m) => !isControlledMed(m));
        recetaData.medicamentos = [...controlledMeds, ...normalMeds];
    }

    const printWindow = openPrintWindow();
    if (!printWindow) return;

    // Iniciar generación de QR en paralelo sin bloquear
    const qrDataPromise = generateQRData(recipeId, recetaData, prescriptionProps);

    const generateQR = (element, data) =>
        new Promise((resolve, reject) => {
            QRCode.toString(data, { type: 'svg', width: 71, margin: 1 }, (err, string) => {
                if (err) {
                    reject(err);
                } else {
                    element.innerHTML = string;
                    resolve();
                }
            });
        });

    const calculateItemsPerPage = async () => {
        try {
            const { container, header, footer, noteHeight, itemHeights } =
                await measureAllInOne(printWindow, recetaData, prescriptionProps);
            const availableSpace = container - (header + footer + 25);

            const pages = [];
            let currentHeight = 0;
            let currentItems = 0;
            let hasNormalMeds = false;

            for (let i = 0; i < itemHeights.length; i++) {
                const height = itemHeights[i];
                const med = recetaData.medicamentos[i];
                const isControlled = isControlledMed(med);

                if (isControlled) {
                    if (currentItems > 0) {
                        pages.push(currentItems);
                        currentItems = 0;
                    }
                    pages.push(1); // Cada controlado debe de tener su propia hoja
                    currentHeight = 0;
                } else {
                    hasNormalMeds = true;
                    if (currentItems === 0) {
                        currentHeight = height;
                        currentItems = 1;
                    } else if (currentHeight + height > availableSpace) {
                        pages.push(currentItems);
                        currentHeight = height;
                        currentItems = 1;
                    } else {
                        currentHeight += height;
                        currentItems++;
                    }
                }
            }
            if (currentItems > 0) {
                pages.push(currentItems);
            }

            // Verificamos en qué página cabe la nota final (la nota al final)
            if (noteHeight > 0) {
                if (!hasNormalMeds) {
                    // Si solo hubo controlados, la nota no debe ir en la hoja controlada
                    pages.push(0);
                } else if (currentHeight + noteHeight > availableSpace) {
                    // Si no cabe en la última página normal, va en una página nueva sola
                    pages.push(0); // 0 indica que es solo la nota
                }
            }

            return pages.length > 0 ? pages : [1];
        } catch (err) {
            console.error('Error calculando items por página:', err);
            return [3];
        }
    };

    const qrData = await qrDataPromise;

    await renderAndPrint(
        printWindow,
        recetaData,
        prescriptionProps,
        calculateItemsPerPage,
        qrData,
        generateQR,
        recipeId,
    );
}

/* ---------------------------------------------
   FUNCIONES AUXILIARES
--------------------------------------------- */

function openPrintWindow() {
    const win = window.open('', '_blank');
    if (!win) {
        alert(
            'Por favor, permite las ventanas emergentes para imprimir la receta.',
        );
        return null;
    }
    return win;
}

async function generateQRData(recipeId, recetaData, prescriptionProps) {
    let cedula = null;

    // 1. Tomar cédula directamente de prescriptionProps para evitar peticiones HTTP duplicadas
    if (prescriptionProps?.doctorSpecialties?.length) {
        cedula =
            prescriptionProps.doctorSpecialties[0]?.identifier ||
            prescriptionProps.doctorSpecialties[0]?.professionalLicense;
    }

    // 2. Si no viene en props, consultar como respaldo
    if (!cedula) {
        const practitionerId = recetaData?.[0]?.practitionerId;
        if (practitionerId) {
            try {
                const practitioner = await getPractitioner(practitionerId);
                cedula = practitioner?.qualification?.[0]?.identifier;
            } catch (error) {
                console.error('Error obteniendo practitioner:', error);
            }
        }
    }

    let cuponCode = '';
    if (cedula) {
        try {
            const res = await assignCuponCode(cedula, recipeId);
            if (res && res.status === 'success') {
                cuponCode = res.cupon_code || '';
            }
        } catch (error) {
            // Ignorar error si no hay cupón disponible
        }
    }

    const authoredOn = recetaData?.[0]?.authoredOn ? convert_to_ISO(recetaData[0].authoredOn) : '';
    const url =
        'https://pill.mx/f/?' +
        'r=' +
        recipeId +
        '&rd=' +
        authoredOn +
        '&c=' +
        cuponCode +
        '&cm=' +
        (cedula || '');
    return url;
}

// Medición en un solo montaje (reduce de N+1 instancias de Vue/Vuetify a solo 1)
async function measureAllInOne(printWindow, recetaData, prescriptionProps) {
    const tempContainer = printWindow.document.createElement('div');
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '148mm';
    tempContainer.style.padding = '2mm';
    tempContainer.style.boxSizing = 'border-box';
    printWindow.document.body.appendChild(tempContainer);

    const hasFinalNote = !!(prescriptionProps.finalNote && prescriptionProps.finalNote.trim());
    const validMeds = (recetaData?.medicamentos || []).filter(
        (m) => m.itemDescription && m.itemDescription !== 'Sin medicamentos',
    );

    const vm = h(RecetaContenedor, {
        ...prescriptionProps,
        prescriptions: validMeds,
        finalNote: hasFinalNote ? prescriptionProps.finalNote : null,
    });
    const app = createApp({ render: () => vm }).use(vuetify);
    app.mount(tempContainer);
    await new Promise((resolve) => printWindow.requestAnimationFrame(resolve));

    const container =
        tempContainer.querySelector('.prescriptionContainer')?.offsetHeight || 770;
    const header = tempContainer.querySelector('header')?.offsetHeight || 150;
    const footer = tempContainer.querySelector('footer')?.offsetHeight || 130;
    const noteHeight = hasFinalNote
        ? tempContainer.querySelector('.note')?.offsetHeight || 0
        : 0;

    const heights = [];
    const itemEls = tempContainer.querySelectorAll(
        '.prescriptions .medication-request-info',
    );
    itemEls.forEach((el) => {
        heights.push(el.offsetHeight || 50);
    });

    if (heights.length < validMeds.length) {
        while (heights.length < validMeds.length) {
            heights.push(50);
        }
    }

    app.unmount();
    tempContainer.remove();

    return { container, header, footer, noteHeight, itemHeights: heights };
}

async function renderAndPrint(
    printWindow,
    recetaData,
    prescriptionProps,
    calculateItemsPerPage,
    qrData,
    generateQR,
    recipeId,
) {
    try {
        const originalUrl = window.location.href;
        setupPrintHtml(printWindow);

        document
            .querySelectorAll('style, link[rel="stylesheet"]')
            .forEach((s) => {
                printWindow.document.head.insertAdjacentHTML(
                    'beforeend',
                    s.outerHTML,
                );
            });

        // Espera no bloqueante para estilos
        if (printWindow.document.readyState !== 'complete') {
            await new Promise((resolve) => {
                const timer = setTimeout(resolve, 300);
                printWindow.onload = () => {
                    clearTimeout(timer);
                    resolve();
                };
            });
        }

        const itemsPerPage = await calculateItemsPerPage();
        const chunks = createChunks(
            recetaData?.medicamentos,
            prescriptionProps.finalNote,
            itemsPerPage,
        );

        const printableContent =
            printWindow.document.getElementById('printable-content');

        for (const [i, chunk] of chunks.entries()) {
            const pageContainer = printWindow.document.createElement('div');
            pageContainer.className = 'printable-page';
            pageContainer.id = `printable-page-${i}`;

            const contentDiv = printWindow.document.createElement('div');
            pageContainer.appendChild(contentDiv);
            printableContent.appendChild(pageContainer);

            const hasNote = chunk.some((item) => item.type === 'note');

            const vm = h(RecetaContenedor, {
                ...prescriptionProps,
                prescriptions: chunk.filter((item) => item.type !== 'note'),
                finalNote: hasNote
                    ? chunk.find((i) => i.type === 'note').text
                    : null,
            });

            createApp({ render: () => vm })
                .use(vuetify)
                .mount(contentDiv);

            await new Promise((resolve) =>
                printWindow.requestAnimationFrame(resolve),
            );
        }

        // Generar códigos QR en paralelo
        const qrEls = printWindow.document.querySelectorAll('#qr');
        await Promise.all(Array.from(qrEls).map((el) => generateQR(el, qrData)));

        await new Promise((resolve) => printWindow.requestAnimationFrame(resolve));
        printWindow.print();

        // Marcar cupón en segundo plano
        useCuponCode(recipeId).catch((error) => {
            console.error('Error al marcar cupón como usado:', error);
        });

        setTimeout(() => {
            printWindow.close();
            window.location.href = originalUrl;
        }, 500);
    } catch (err) {
        console.error('Error generando PDF:', err);
        printWindow?.close();
    }
}

function setupPrintHtml(printWindow) {
    printWindow.document.write(`
        <!doctype html>
        <html>
            <head>
                <title>Receta</title>
                <style>
                    html, body {
                        margin: 0;
                        padding: 0;
                        width: 100%;
                        height: auto;
                    }

                    #printable-content {
                        width: 148mm;
                        margin: 0 auto;
                        box-sizing: border-box;
                        position: relative;
                    }

                    .printable-page {
                        padding: 2mm;
                        box-sizing: border-box;
                        width: 100%;
                        height: 100%;
                        overflow: hidden;
                        position: relative;
                    }

                    @media print {
                        @page {
                            size: A5 portrait;
                            margin: 2mm;
                        }

                        #printable-content {
                            height: 210mm;
                        }

                        .printable-page {
                            page-break-after: always;
                        }

                        .printable-page:last-child {
                            page-break-after: auto;
                        }
                    }
                </style>
            </head>
            <body>
                <div id="printable-content"></div>
                <div id="temp-measure-container" style="visibility:hidden; position:absolute; left:-9999px; top:-9999px;"></div>
            </body>
        </html>
    `);
    printWindow.document.close();
}

function createChunks(medicamentos = [], note, itemsPerPage) {
    const validMeds = (medicamentos || []).filter(
        (m) => m.itemDescription && m.itemDescription !== 'Sin medicamentos',
    );
    const chunks = [];
    let idx = 0;
    for (const size of itemsPerPage) {
        if (size > 0) {
            chunks.push(validMeds.slice(idx, idx + size));
            idx += size;
        }
    }

    if (itemsPerPage[itemsPerPage.length - 1] === 0) {
        chunks.push([{ type: 'note', text: note }]);
    } else if (note) {
        if (chunks.length > 0) {
            chunks[chunks.length - 1].push({ type: 'note', text: note });
        } else {
            chunks.push([{ type: 'note', text: note }]);
        }
    }

    if (!validMeds.length && !note) {
        return [[]];
    }

    return chunks;
}
